import {CLASS_NAMES} from '@github-ui/commenting/DomElements'
import {type Repository, useCurrentRepository} from '@github-ui/current-repository'
import {commitDiscussionsPath} from '@github-ui/paths'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {LockIcon} from '@primer/octicons-react'
import {Box, Button, CounterLabel, Spinner} from '@primer/react'
import clsx from 'clsx'
import {useCallback, useEffect, useState} from 'react'

import type {CommitComment} from '../../../types/commit-types'
import type {Commit} from '../../../types/shared'
import styles from './Comment.module.css'
import {ExistingCommitComments} from './ExistingCommitComments'
import {NewCommitComment} from './NewCommitComment'

export function DiscussionComments({commit}: {commit: Commit}) {
  const {comments, setComments, commentCount, setCommentCount, hasMoreComments, loadDiscussionComments, loaded} =
    useDiscussionComments(commit, useCurrentRepository())

  // Used for quote replies
  const [newCommentContent, setNewCommentContent] = useState<string | undefined>(undefined)

  const addComment = (comment: CommitComment) => {
    setComments([...comments, comment])
    setCommentCount(commentCount + 1)
  }

  const deleteComment = (comment: CommitComment) => {
    setComments(comments.filter(c => c.id !== comment.id))
    setCommentCount(commentCount - 1)
  }

  useEffect(() => {
    if (ssrSafeWindow?.location.hash) {
      const urlFragment = ssrSafeWindow.location.hash.slice(1)
      const comment = comments.find(c => c.urlFragment === urlFragment)

      if (comment) {
        const commentElement = document.getElementById(urlFragment)
        if (commentElement) {
          commentElement.scrollIntoView()
          commentElement.focus()
        }
      }
    }
  }, [comments])

  if (!loaded && comments.length === 0) {
    return (
      <div className="d-flex flex-justify-center p-3">
        <Spinner size="large" />
      </div>
    )
  }

  return (
    <Box
      className={clsx([
        'd-flex flex-column gap-3 pt-3',
        CLASS_NAMES.commentsContainer,
        styles['commit-discussion-comments'],
      ])}
    >
      <DiscussionCommentsHeader commentCount={commentCount} />
      {!loaded ? (
        <div className="d-flex flex-justify-center">
          <Spinner size="large" />
        </div>
      ) : hasMoreComments ? (
        <Button className="width-full" onClick={() => loadDiscussionComments(comments[0]?.id)}>
          Load more comments
        </Button>
      ) : null}
      <ExistingCommitComments
        comments={comments}
        commit={commit}
        deleteComment={deleteComment}
        setComments={setComments}
        setNewCommentContent={setNewCommentContent}
      />
      {/* TODO: roses */}
      <NewCommitComment commitOid={commit.oid} addComment={addComment} newCommentContent={newCommentContent} />
      {/* TODO: subscribe/unsubscribe */}
    </Box>
  )
}

function DiscussionCommentsHeader({commentCount}: {commentCount: number}) {
  return (
    <div className="d-flex flex-items-center flex-justify-between">
      <h2 className="sr-only">{commentCount} commit comments</h2>
      <div className="d-flex flex-items-center">
        <div className="h4 pr-2">Comments</div>
        <CounterLabel>{commentCount}</CounterLabel>
      </div>
      {/* Todo lock/unlock converstion */}
      <Button leadingVisual={LockIcon} variant="invisible">
        Lock conversation
      </Button>
    </div>
  )
}

interface DiscussionCommentData {
  comments: CommitComment[]
  commentCount: number
  canLoadMoreComments: boolean
}

function useDiscussionComments(commit: Commit, repo: Repository) {
  const [comments, setComments] = useState<CommitComment[]>([])
  const [loaded, setLoaded] = useState(false)
  const [hasMoreComments, setHasMoreComments] = useState(false)
  const [commentCount, setCommentCount] = useState(0)

  const loadDiscussionComments = useCallback(
    async (beforeCommentId?: string) => {
      setLoaded(false)
      const response = await verifiedFetchJSON(
        commitDiscussionsPath({
          owner: repo.ownerLogin,
          repo: repo.name,
          commitOid: commit.oid,
          beforeCommentId,
        }),
      )

      if (response.ok) {
        const data: DiscussionCommentData = await response.json()
        if (beforeCommentId) {
          setComments(c => [...data.comments, ...c])
        } else {
          setComments(data.comments)
        }
        setHasMoreComments(data.canLoadMoreComments)
        setCommentCount(data.commentCount)
        setLoaded(true)
      } else {
        // TODO - handle error
      }
    },
    [repo.ownerLogin, repo.name, commit.oid],
  )

  useEffect(() => {
    if (!loaded) {
      loadDiscussionComments()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDiscussionComments])

  return {comments, setComments, commentCount, setCommentCount, hasMoreComments, loadDiscussionComments, loaded}
}

try{ DiscussionComments.displayName ||= 'DiscussionComments' } catch {}
try{ DiscussionCommentsHeader.displayName ||= 'DiscussionCommentsHeader' } catch {}